import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Input, Select, Badge, Modal, Spinner
} from '../../components/UI';
import { leadsAPI, usersAPI, pipelineAPI, productsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus, HiOutlineUser, HiOutlinePhone, HiOutlineMail,
  HiOutlineTrash, HiOutlineUpload, HiOutlineDownload, HiOutlineUserAdd,
  HiOutlineExclamation, HiOutlineCheck, HiOutlineX, HiOutlineCube
} from 'react-icons/hi';
import Papa from 'papaparse';

// Phone validation function - validates Indian phone numbers (10 digits)
const validatePhoneNumber = (phone) => {
  if (!phone) return { isValid: false, error: 'Phone number is required' };

  // Remove spaces, hyphens, and +91 prefix
  const cleanPhone = phone.toString().replace(/[\s\-]/g, '').replace(/^\+91/, '');

  // Check if it's exactly 10 digits
  if (!/^\d{10}$/.test(cleanPhone)) {
    return { isValid: false, error: 'Phone must be 10 digits' };
  }

  // Check if it starts with a valid Indian mobile prefix (6-9)
  if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
    return { isValid: false, error: 'Phone must start with 6-9' };
  }

  return { isValid: true, cleanPhone };
};

// Name validation function
const validateName = (name) => {
  if (!name) return { isValid: false, error: 'Name is required' };

  const cleanName = name.trim();

  if (cleanName.length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters' };
  }

  // Name is allowed to contain letters, numbers, spaces, emojis, and
  // special characters (e.g. "John D.", "O'Brien", "Raj 🌟", "Raj2").
  return { isValid: true, cleanName };
};

// Email validation function
const validateEmail = (email) => {
  if (!email) return { isValid: true, cleanEmail: '' }; // Email is optional

  const cleanEmail = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
    return { isValid: false, error: 'Invalid email format' };
  }

  return { isValid: true, cleanEmail };
};

// Status validation function
const validateStatus = (status) => {
  if (!status) return { isValid: true, cleanStatus: 'new' }; // Default to 'new' if not provided

  // Normalize the status: trim, lowercase, and handle underscores/spaces
  const normalizedInput = status.trim().toLowerCase().replace(/[_\s]+/g, ' ');

  // Map of valid status variations to their backend values
  const statusMap = {
    'new': 'new',
    'follow up': 'follow_up',
    'followup': 'follow_up',
    'not connected': 'not_connected',
    'notconnected': 'not_connected',
    'cold': 'cold',
    'hot': 'hot',
    'warm': 'warm',
    'converted': 'converted'
  };

  const normalizedStatus = statusMap[normalizedInput];

  if (!normalizedStatus) {
    return {
      isValid: false,
      error: 'Status must be one of: new, follow up, not connected, cold, hot, warm, converted'
    };
  }

  return { isValid: true, cleanStatus: normalizedStatus };
};

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'cold', label: 'Cold' },
  { value: 'warm', label: 'Warm' },
  { value: 'hot', label: 'Hot' },
];

const AssignLeads = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state for single lead
  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    status: 'new',
    stage: '',
    notes: '',
    assignedTo: '',
    productIds: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  // Bulk import state
  const [bulkLeads, setBulkLeads] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [bulkStage, setBulkStage] = useState(''); // default stage applied to imported leads
  const [bulkProductIds, setBulkProductIds] = useState([]); // products applied to imported leads
  const [validationResults, setValidationResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Fetch telecallers for assignment
  const { data: telecallersData, isLoading: telecallersLoading } = useQuery({
    queryKey: ['telecallers'],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: 'telecaller' });
      return response.data?.data || [];
    },
  });

  // Fetch all leads for duplicate checking
  const { data: existingLeadsData } = useQuery({
    queryKey: ['allLeadsForValidation'],
    queryFn: async () => {
      const response = await leadsAPI.getAll({ limit: 10000 });
      return response.data?.data || [];
    },
    staleTime: 30000, // Cache for 30 seconds
  });

  // Fetch pipeline stages for the stage selector
  const { data: stagesData } = useQuery({
    queryKey: ['pipelineStages'],
    queryFn: async () => {
      const response = await pipelineAPI.getAll();
      return response.data?.data || [];
    },
  });

  const telecallers = telecallersData || [];
  const stages = stagesData || [];

  // Fetch active products for optional product selection
  const { data: productsData } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: async () => {
      const response = await productsAPI.getAll({ active: 'true' });
      return response.data?.data || [];
    },
  });
  const products = productsData || [];

  // Stage dropdown options from the organization's pipeline
  const stageOptions = stages.map((stage) => ({
    value: stage.key,
    label: stage.name,
  }));

  // Default stage for a new lead: first pipeline stage, otherwise 'new_lead'
  const defaultStageKey = stages[0]?.key || 'new_lead';

  // Get existing phone numbers for duplicate check
  const existingPhones = useMemo(() => {
    if (!existingLeadsData) return new Set();
    return new Set(
      existingLeadsData.map(lead => lead.phone?.replace(/[\s\-]/g, '').replace(/^\+91/, ''))
    );
  }, [existingLeadsData]);

  // Create lead mutation
  const createMutation = useMutation({
    mutationFn: (data) => leadsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries(['leadsCounts']);
      toast.success('Lead created successfully');
      navigate('/app/leads');
    },
    onError: () => toast.error('Failed to create lead'),
  });

  // Bulk create mutation
  const bulkCreateMutation = useMutation({
    mutationFn: (leads) => leadsAPI.bulkCreate(leads),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries(['leadsCounts']);
      const { totalImported, totalSkipped, duplicates } = response.data?.data || {};
      console.log('Bulk create response:', response.data);
      let message = `${response.data.message || 'Leads imported successfully'}.`;
      if (totalSkipped > 0 || duplicates > 0) {
        message += `. ${duplicates || 0} duplicates skipped`;
      }
      toast.success(message);
      navigate('/app/leads');
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.message || 'Failed to create leads';
      toast.error(errorMsg);
    },
  });

  const handleCreateLead = async () => {
    const errors = {};

    // Validate name
    const nameValidation = validateName(leadForm.name);
    if (!nameValidation.isValid) {
      errors.name = nameValidation.error;
    }

    // Phone: required, but no format validation (any format allowed)
    if (!leadForm.phone || !leadForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    // Email: optional, no format validation

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    createMutation.mutate({
      ...leadForm,
      stage: leadForm.stage || defaultStageKey,
      phone: leadForm.phone.trim(),
      name: nameValidation.cleanName,
      email: leadForm.email.trim(),
    });
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    // Prevent numbers in name
    if (/\d/.test(value)) return;
    setLeadForm({ ...leadForm, name: value });
    if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setLeadForm({ ...leadForm, phone: value });
    if (formErrors.phone) setFormErrors({ ...formErrors, phone: '' });
  };

  const handleEmailChange = (e) => {
    setLeadForm({ ...leadForm, email: e.target.value });
    if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = results.data.map((row, index) => ({
          rowNum: index + 2, // Row number in CSV (accounting for header)
          name: row.name || row.Name || '',
          phone: row.phone || row.Phone || row.mobile || row.Mobile || '',
          email: row.email || row.Email || '',
          stage: bulkStage || defaultStageKey,
          status: row.status || row.Status || 'new',
          notes: row.notes || row.Notes || '',
          rawPhone: row.phone || row.Phone || row.mobile || row.Mobile || '',
        })).filter(lead => lead.name || lead.phone);

        if (parsed.length === 0) {
          toast.error('No valid leads found in file');
          return;
        }

        // Validate each lead
        const validatedLeads = parsed.map(lead => {
          const errors = [];

          // Validate name
          const nameValidation = validateName(lead.name);
          if (!nameValidation.isValid) {
            errors.push(`Name: ${nameValidation.error}`);
          }

          // Validate phone
          const phoneValidation = validatePhoneNumber(lead.phone);
          if (!phoneValidation.isValid) {
            errors.push(`Phone: ${phoneValidation.error}`);
          } else {
            lead.phone = phoneValidation.cleanPhone;
          }

          // Validate email
          const emailValidation = validateEmail(lead.email);
          if (!emailValidation.isValid) {
            errors.push(`Email: ${emailValidation.error}`);
          } else {
            lead.email = emailValidation.cleanEmail;
          }

          // Validate status
          const statusValidation = validateStatus(lead.status);
          if (!statusValidation.isValid) {
            errors.push(`Status: ${statusValidation.error}`);
          } else {
            lead.status = statusValidation.cleanStatus;
          }

          // Check for duplicates within file
          lead.isDuplicateInFile = false;
          lead.isDuplicateInSystem = false;

          return {
            ...lead,
            errors,
            isValid: errors.length === 0,
            name: nameValidation.isValid ? nameValidation.cleanName : lead.name,
          };
        });

        // Check for duplicates within file
        const phoneCounts = {};
        validatedLeads.forEach(lead => {
          if (lead.phone) {
            phoneCounts[lead.phone] = (phoneCounts[lead.phone] || 0) + 1;
          }
        });

        // Mark duplicates within file
        validatedLeads.forEach(lead => {
          if (lead.phone && phoneCounts[lead.phone] > 1) {
            lead.isDuplicateInFile = true;
            lead.isValid = false;
            if (!lead.errors.includes('Duplicate phone in file')) {
              lead.errors.push('Duplicate phone in file');
            }
          }
        });

        // Check for duplicates in system
        validatedLeads.forEach(lead => {
          if (lead.phone && existingPhones.has(lead.phone)) {
            lead.isDuplicateInSystem = true;
            lead.isValid = false;
            if (!lead.errors.includes('Phone already exists in system')) {
              lead.errors.push('Phone already exists in system');
            }
          }
        });

        setBulkLeads(validatedLeads);

        // Show validation summary
        const validCount = validatedLeads.filter(l => l.isValid).length;
        const invalidCount = validatedLeads.length - validCount;

        if (invalidCount > 0) {
          setValidationResults({
            total: validatedLeads.length,
            valid: validCount,
            invalid: invalidCount,
            duplicates: validatedLeads.filter(l => l.isDuplicateInSystem).length,
            duplicateInFile: validatedLeads.filter(l => l.isDuplicateInFile).length,
          });
        } else {
          setValidationResults(null);
        }

        setShowBulkModal(true);

        if (invalidCount > 0) {
          toast.error(`Found ${invalidCount} invalid/duplicate leads. Please review before importing.`);
        } else {
          toast.success(`Found ${validCount} valid leads ready to import`);
        }
      },
      error: () => toast.error('Failed to parse file'),
    });
  };

  const handleBulkCreate = async () => {
    if (bulkLeads.length === 0) return;

    // Only import valid leads
    const validLeads = bulkLeads.filter(lead => lead.isValid);

    if (validLeads.length === 0) {
      toast.error('No valid leads to import. Please fix errors and try again.');
      return;
    }

    // Format leads for API
    const leadsToCreate = validLeads.map(lead => ({
      name: lead.name,
      phone: lead.phone,
      email: lead.email,
      stage: lead.stage || bulkStage || defaultStageKey,
      status: lead.status || 'new',
      notes: lead.notes,
      assignedTo: bulkAssignTo || undefined,
      productIds: bulkProductIds.length > 0 ? bulkProductIds : undefined,
    }));

    bulkCreateMutation.mutate({ leads: leadsToCreate });
  };

  const handleDownloadTemplate = () => {
    // CSV template with every field the importer reads, in column order.
    // The first row is the header (field titles). One example row is included.
    const csv = Papa.unparse(
      [
        {
          name: 'John Doe',
          phone: '9876543210',
          email: 'john@example.com',
          status: 'new',
          notes: 'Interested in a demo',
        },
      ],
      { columns: ['name', 'phone', 'email', 'status', 'notes'] }
    );
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_import_template.csv';
    a.click();
  };

  const removeBulkLead = (index) => {
    setBulkLeads(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add & Assign Leads</h1>
            <p className="text-gray-500 text-sm mt-1">
              Create individual leads or import in bulk
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/app/leads')}>
            View All Leads
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Single Lead Form */}
          <Card padding="p-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <HiOutlineUser className="w-5 h-5" />
              Add Single Lead
            </h2>
            <div className="space-y-4">
              <Input
                label="Name *"
                value={leadForm.name}
                onChange={handleNameChange}
                placeholder="Enter lead name"
                error={formErrors.name}
              />
              <Input
                label="Phone *"
                value={leadForm.phone}
                onChange={handlePhoneChange}
                placeholder="Enter phone number"
                icon={<HiOutlinePhone className="w-5 h-5" />}
                error={formErrors.phone}
              />
              <Input
                label="Email"
                type="email"
                value={leadForm.email}
                onChange={handleEmailChange}
                placeholder="Enter email (optional)"
                icon={<HiOutlineMail className="w-5 h-5" />}
                error={formErrors.email}
              />
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Stage"
                  options={stageOptions}
                  value={leadForm.stage || defaultStageKey}
                  onChange={(e) => setLeadForm({ ...leadForm, stage: e.target.value })}
                />
              </div>
              <Select
                label="Status"
                options={statusOptions}
                value={leadForm.status}
                onChange={(e) => setLeadForm({ ...leadForm, status: e.target.value })}
              />

              {/* Assign To Telecaller */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <HiOutlineUserAdd className="w-4 h-4" />
                  Assign to Telecaller
                </label>
                {telecallersLoading ? (
                  <div className="text-sm text-gray-500 py-2">Loading telecallers...</div>
                ) : (
                  <Select
                    options={[
                      { value: '', label: 'Leave unassigned' },
                      ...telecallers.map(u => ({
                        value: u._id,
                        label: `${u.firstName} ${u.lastName}`,
                      })),
                    ]}
                    value={leadForm.assignedTo}
                    onChange={(e) => setLeadForm({ ...leadForm, assignedTo: e.target.value })}
                    placeholder="Select telecaller (optional)"
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Optionally assign this lead to a telecaller now, or leave unassigned
                </p>
              </div>

              {/* Product selection (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <HiOutlineCube className="w-4 h-4" />
                  Products
                </label>
                {products.length === 0 ? (
                  <p className="text-xs text-gray-500">No products available (optional)</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {products.map((product) => {
                      const selected = leadForm.productIds.includes(product._id);
                      return (
                        <button
                          type="button"
                          key={product._id}
                          onClick={() =>
                            setLeadForm((prev) => ({
                              ...prev,
                              productIds: selected
                                ? prev.productIds.filter((id) => id !== product._id)
                                : [...prev.productIds, product._id],
                            }))
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm font-medium transition-all ${
                            selected
                              ? 'bg-primary-50 border-primary-400 text-primary-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <HiOutlineCube className="w-4 h-4" />
                          {product.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Optionally link this lead to one or more products
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notes
                </label>
                <textarea
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none"
                  rows={3}
                  value={leadForm.notes}
                  onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })}
                  placeholder="Add notes..."
                />
              </div>
              <Button
                variant="primary"
                className="w-full"
                onClick={handleCreateLead}
                loading={createMutation.isPending || isCheckingDuplicate}
              >
                {/* <HiOutlinePlus className="w-5 h-5 mr-2" /> */}
                {isCheckingDuplicate ? 'Checking...' : 'Create Lead'}
              </Button>
            </div>
          </Card>

          {/* Bulk Import */}
          <Card padding="p-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <HiOutlineUpload className="w-5 h-5" />
              Bulk Import
            </h2>
            <div className="space-y-4">
              {/* File Upload Area */}
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-primary-500 transition-colors">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <HiOutlineUpload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 font-medium">Upload CSV File</p>
                  <p className="text-sm text-gray-400 mt-1">Click to browse or drag and drop</p>
                </label>
              </div>

              {/* Default stage for imported leads */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Default Stage for Imported Leads
                </label>
                <Select
                  options={stageOptions}
                  value={bulkStage || defaultStageKey}
                  onChange={(e) => setBulkStage(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Applied to every imported lead unless the CSV has a <strong>stage</strong> column that overrides it
                </p>
              </div>

              {/* Products for imported leads (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  <HiOutlineCube className="w-4 h-4" />
                  Products for Imported Leads (Optional)
                </label>
                {products.length === 0 ? (
                  <p className="text-xs text-gray-500">No products available</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {products.map((product) => {
                      const selected = bulkProductIds.includes(product._id);
                      return (
                        <button
                          type="button"
                          key={product._id}
                          onClick={() =>
                            setBulkProductIds((prev) =>
                              selected
                                ? prev.filter((id) => id !== product._id)
                                : [...prev, product._id]
                            )
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm font-medium transition-all ${
                            selected
                              ? 'bg-primary-50 border-primary-400 text-primary-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <HiOutlineCube className="w-4 h-4" />
                          {product.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Selected products are applied to every imported lead
                </p>
              </div>

              {/* Template Download */}
              <Button
                variant="outline"
                className="w-full"
                icon={<HiOutlineDownload className="w-5 h-5" />}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>

              {/* Instructions */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-medium text-gray-700 mb-2">CSV Format</h3>
                <p className="text-sm text-gray-500 mb-2">
                  Required columns: <strong>name</strong>, <strong>phone</strong>
                </p>
                <p className="text-sm text-gray-500 mb-2">
                  Optional columns: <strong>email</strong>, <strong>status</strong>, <strong>notes</strong>
                </p>
                <div className="text-xs text-gray-500 mb-2 space-y-1">
                  <p><strong>Stage:</strong> set using the <strong>Default Stage for Imported Leads</strong> selector above (not a CSV column).</p>
                  <p><strong>Status values:</strong> new, open, in_progress, follow_up, cold, warm, hot</p>
                  <p><strong>Products:</strong> set using the selector above (not a CSV column).</p>
                </div>
                <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                  <code className="text-xs text-gray-600">
                    name,phone,email,status,notes<br/>
                    John Doe,9876543210,john@example.com,new,Interested in a demo
                  </code>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Bulk Preview Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title={`Preview Leads (${bulkLeads.length})`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Assign All Bulk Leads */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
              <HiOutlineUserAdd className="w-4 h-4" />
              Assign All To (Optional)
            </label>
            <Select
              options={[
                { value: '', label: 'Leave all unassigned' },
                ...telecallers.map(u => ({
                  value: u._id,
                  label: `${u.firstName} ${u.lastName}`,
                })),
              ]}
              value={bulkAssignTo}
              onChange={(e) => {
                const telecallerId = e.target.value;
                setBulkAssignTo(telecallerId);
                // Update all bulk leads with the selected telecaller
                if (telecallerId) {
                  setBulkLeads(prev => prev.map(lead => ({ ...lead, assignedTo: telecallerId })));
                }
              }}
              placeholder="Select telecaller to assign all leads"
            />
          </div>

          {/* Validation Summary */}
          {validationResults && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                <HiOutlineExclamation className="w-5 h-5" />
                <span>Validation Summary</span>
              </div>
              <div className="text-sm text-amber-600 space-y-1">
                <p>Total leads: <strong>{validationResults.total}</strong></p>
                <p className="text-green-600">Valid leads: <strong>{validationResults.valid}</strong></p>
                {validationResults.duplicateInFile > 0 && (
                  <p className="text-red-600">Duplicates within file: <strong>{validationResults.duplicateInFile}</strong></p>
                )}
                {validationResults.duplicates > 0 && (
                  <p className="text-red-600">Already in system: <strong>{validationResults.duplicates}</strong></p>
                )}
                <p className="text-amber-600">Invalid leads: <strong>{validationResults.invalid}</strong></p>
              </div>
            </div>
          )}

          {/* Leads List */}
          <div className="max-h-80 overflow-y-auto space-y-2">
            {bulkLeads.map((lead, index) => (
              <div
                key={index}
                className={`flex items-start justify-between p-3 rounded-xl border ${
                  lead.isValid
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {lead.isValid ? (
                      <HiOutlineCheck className="w-4 h-4 text-green-500" />
                    ) : (
                      <HiOutlineX className="w-4 h-4 text-red-500" />
                    )}
                    <p className="font-medium text-gray-900">{lead.name || 'No Name'}</p>
                  </div>
                  <div className="text-sm text-gray-500 ml-6 flex flex-wrap gap-x-2">
                    <span>{lead.rawPhone || lead.phone}</span>
                    {lead.stage && <span className="text-primary-600">• {lead.stage}</span>}
                    {lead.status && <span className="text-blue-600">• {lead.status}</span>}
                  </div>
                  {lead.errors && lead.errors.length > 0 && (
                    <div className="mt-1 ml-6">
                      {lead.errors.map((error, idx) => (
                        <p key={idx} className="text-xs text-red-500">{error}</p>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={lead.status} size="sm">
                    {lead.status}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                    onClick={() => removeBulkLead(index)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-100 mt-4">
          <Button variant="ghost" onClick={() => setShowBulkModal(false)}>
            Cancel
          </Button>
          {validationResults && validationResults.invalid > 0 && (
            <Button
              variant="outline"
              onClick={handleBulkCreate}
              loading={bulkCreateMutation.isPending}
            >
              Import {validationResults.valid} Valid Leads
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleBulkCreate}
            loading={bulkCreateMutation.isPending}
            disabled={bulkLeads.filter(l => l.isValid).length === 0}
          >
            {validationResults && validationResults.invalid > 0
              ? `Import ${validationResults.valid} Valid Leads`
              : `Create ${bulkLeads.length} Leads`}
          </Button>
        </div>
      </Modal>
    </Layout>
  );
};

export default AssignLeads;